import React, { useContext, useEffect, useRef, useState } from "react";
import { ApiContext } from "../../context/ApiContext";
import apiRequest from "../../requests/apiRequest";
import Loader from "../../shared/Loader";
import __ from "../../utils/getContent";

interface MemberReferralFieldProps {
	onSelect: (selection: string) => void;
}

const MemberReferralField: React.FC<MemberReferralFieldProps> = ({
	onSelect,
}) => {
	const { data } = useContext(ApiContext);
	const [input, setInput] = useState("");
	const [suggestions, setSuggestions] = useState<
		{
			Name: string;
			MemberId: number;
			ClubName: string;
		}[]
	>([]);
	const [isFetching, setIsFetching] = useState(false);
	const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
		null,
	);
	const [skipSearch, setSkipSearch] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [noMatchMessage, setNoMatchMessage] = useState("");

	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (skipSearch) {
			setSkipSearch(false); // Reset the flag after handling
			return;
		}

		if (input.length >= 3) {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}

			const newTimeout = setTimeout(() => {
				fetchSuggestions(input);
			}, 300);
			setDebounceTimeout(newTimeout);
		} else {
			setSuggestions([]);
		}

		return () => {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
		};
	}, [input]); // eslint-disable-line react-hooks/exhaustive-deps

	const fetchSuggestions = async (input: string) => {
		setIsFetching(true);
		setNoMatchMessage("");

		const response = await apiRequest("memberLookup", [
			{ search: input },
			{ EmployeeAuthTicket: data.EmployeeAuthTicket },
		]);

		if (response?.data && response?.data?.Members.length) {
			setSuggestions(response?.data?.Members);
		} else {
			setNoMatchMessage(__("no_matches_found", "No matches found."));
			setSuggestions([]);
			onSelect("");
		}

		setIsFetching(false);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target) {
			setInput(e.target.value);
		}
	};

	const handleSelectionChange = (selection: {
		Name: string;
		MemberId: number;
	}) => {
		setSkipSearch(true);
		setInput(selection.Name);
		onSelect(selection.Name);
		setSuggestions([]);
	};

	const handleDropdownFocus = () => setIsFocused(true);
	const handleDropdownBlur = () => {
		// Delay to check if focus moves to another dropdown item
		setTimeout(() => {
			if (!dropdownRef.current?.contains(document.activeElement)) {
				setSuggestions([]);
				setIsFocused(false);
			}
		}, 0);
	};

	const handleInputBlur = () => {
		// Close the dropdown only if the dropdown itself isn't focused
		setTimeout(() => {
			if (!dropdownRef.current?.contains(document.activeElement)) {
				setSuggestions([]);
				setIsFocused(false);
			}
		}, 0);
	};

	return (
		<>
			<div className="relative mx-auto w-full max-w-md">
				<input
					type="text"
					value={input}
					onChange={handleInputChange}
					onFocus={() => setIsFocused(true)}
					onBlur={handleInputBlur}
					placeholder={__("type_to_search", "Type to search...")}
					className={`mb-2 w-full border border-black px-2 py-3 placeholder:uppercase`}
				/>
				{isFetching && <Loader colorCircle="#000" colorRotator="#ddd" />}
				{suggestions.length > 0 && !isFetching && isFocused && (
					<div
						className="absolute top-full z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
						onFocus={handleDropdownFocus}
						onBlur={handleDropdownBlur}
						ref={dropdownRef}
					>
						{suggestions.map((suggestion, index) => (
							<button
								key={index}
								className="w-full cursor-pointer px-4 py-2 text-left hover:bg-blue-100"
								onClick={() => handleSelectionChange(suggestion)}
							>
								{suggestion.Name}
								<span className="ml-1.5 inline-block">
									{`(${suggestion?.ClubName} | ${suggestion?.MemberId})`}
								</span>
							</button>
						))}
					</div>
				)}
			</div>
			{noMatchMessage && (
				<div className="mt-2 text-sm text-red-500">{noMatchMessage}</div>
			)}
		</>
	);
};

export default MemberReferralField;
